import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Pencil, Printer, Trash2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  NumericMetricDescriptionInfo,
  toast,
} from "@cliently/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useClientProfile } from "../hooks/use-client-profile";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";
import { resolveNumericTrackingConfigured } from "@cliently/types";
import type { ClientPortalFeatures } from "@cliently/schemas";
import {
  type NumericMetricEntry,
  findNumericMetricEntryForDay,
  localDateKey,
  parseNumericMetricEntries,
  recordedOnKey,
} from "../lib/numeric-metric-entries";
import {
  type MetricsRange,
  computeAvailableRanges,
  cutoffForRange,
  formatChartDateLabel,
  resolveDefaultRange,
} from "../lib/numeric-metrics-chart";
import { NumericMetricsRangeSelector } from "../components/numeric-metrics-range-selector";



const parseAssignedMetricConfig = (
  meta: unknown,
  metricGroups: Array<{ metricIds: string[] }> | undefined,
): {
  configured: boolean;
  ids: string[];
} => {
  const root =
    meta && typeof meta === "object" && !Array.isArray(meta)
      ? (meta as Record<string, unknown>)
      : {};
  const priv =
    root.private && typeof root.private === "object" && !Array.isArray(root.private)
      ? (root.private as Record<string, unknown>)
      : {};
  const raw = priv.numericMetricDefinitionIds;
  const directConfigured = Array.isArray(raw);
  const directIds = directConfigured
    ? (raw as unknown[]).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
    : [];
  const groups = (metricGroups ?? []).map((g) => g.metricIds ?? []);
  const hasGroups = groups.some((arr) => arr.length > 0);
  const configured = directConfigured || hasGroups;
  if (!configured) return { configured: false, ids: [] };
  const set = new Set<string>(directIds);
  for (const arr of groups)
    for (const id of arr) {
      if (typeof id === "string" && id.length > 0) set.add(id);
    }
  return { configured: true, ids: Array.from(set) };
}

export const NumericMetricsPage = () => {
  const { t } = useTranslation();
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: profile, isLoading } = useClientProfile();
  const qc = useQueryClient();

  const features = profile?.org?.clientPortalFeatures as
    | ClientPortalFeatures
    | undefined;

  const allDefinitions = features?.numericDefinitions ?? [];
  const assignedMetricConfig = useMemo(
    () => parseAssignedMetricConfig(profile?.metaData, profile?.metricGroups),
    [profile?.metaData, profile?.metricGroups],
  );
  const definitions = useMemo(() => {
    const allowed = new Set(assignedMetricConfig.ids);
    return allDefinitions.filter((d) => allowed.has(d.id));
  }, [allDefinitions, assignedMetricConfig]);
  const entries = useMemo(
    () => parseNumericMetricEntries(profile?.metaData),
    [profile?.metaData],
  );
  const entryTimestamp = useCallback((entry: NumericMetricEntry) => {
    const ymd = recordedOnKey(entry);
    const ts = new Date(`${ymd}T00:00:00`).getTime();
    if (Number.isFinite(ts)) return ts;
    return new Date(entry.at).getTime();
  }, []);

  const oldestEntryAt = useMemo(() => {
    let min = Number.POSITIVE_INFINITY;
    for (const e of entries) {
      const ts = entryTimestamp(e);
      if (Number.isFinite(ts) && ts < min) min = ts;
    }
    return Number.isFinite(min) ? min : null;
  }, [entries, entryTimestamp]);

  const availableRanges = useMemo(
    () => computeAvailableRanges(oldestEntryAt),
    [oldestEntryAt],
  );

  const availableRangesKey = useMemo(() => availableRanges.join("|"), [availableRanges]);

  const [range, setRange] = useState<MetricsRange>("all");
  const defaultRangeAppliedRef = useRef(false);

  useEffect(() => {
    const resolved = resolveDefaultRange({
      availableRanges,
      currentRange: range,
      defaultApplied: defaultRangeAppliedRef.current,
    });
    defaultRangeAppliedRef.current = resolved.defaultApplied;
    if (resolved.nextRange !== range) {
      setRange(resolved.nextRange);
    }
  }, [availableRangesKey, availableRanges, range]);

  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [dateByMetricId, setDateByMetricId] = useState<Record<string, string>>(
    {},
  );
  const [historyDefId, setHistoryDefId] = useState<string | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<string>("");
  const [newEntryDate, setNewEntryDate] = useState(localDateKey(new Date()));
  const [newEntryValue, setNewEntryValue] = useState("");
  const rangeLabelByValue: Record<MetricsRange, string> = {
    "1y": t("metrics.range1y", "1 an"),
    "3y": t("metrics.range3y", "3 ans"),
    "5y": t("metrics.range5y", "5 ans"),
    all: t("metrics.rangeAll", "Tout"),
  };

  const appendMutation = useMutation({
    mutationFn: async ({
      metricId,
      value,
      recordedOn,
    }: {
      metricId: string;
      value: number;
      recordedOn: string;
    }) => {
      await api.post("/portal/numeric-metric-entries", {
        metricId,
        value,
        recordedOn,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-profile"] });
      toast.success(t("metrics.saved", "Valeur enregistrée"));
    },
    onError: (err) => {
      const msg =
        axios.isAxiosError(err) &&
        typeof err.response?.data?.message === "string"
          ? err.response.data.message
          : t("metrics.saveError", "Impossible d'enregistrer");
      toast.error(msg);
    },
  });

  const patchMutation = useMutation({
    mutationFn: async ({
      entryId,
      value,
    }: {
      entryId: string;
      value: number;
    }) => {
      await api.patch(`/portal/numeric-metric-entries/${entryId}`, { value });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-profile"] });
      toast.success(t("metrics.updated", "Valeur mise à jour"));
      setEditingEntryId(null);
      setEditDraft("");
    },
    onError: () => {
      toast.error(t("metrics.updateError", "Impossible de modifier"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (entryId: string) => {
      await api.delete(`/portal/numeric-metric-entries/${entryId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["client-profile"] });
      toast.success(t("metrics.deleted", "Mesure supprimée"));
    },
    onError: () => {
      toast.error(t("metrics.deleteError", "Impossible de supprimer"));
    },
  });

  const cutoff = useMemo(() => cutoffForRange(range), [range]);

  const charts = useMemo(() => {
    return definitions.map((def) => {
      const series = entries
        .filter((e) => e.metricId === def.id && entryTimestamp(e) >= cutoff)
        .sort((a, b) => entryTimestamp(a) - entryTimestamp(b))
        .map((e) => ({
          label: formatChartDateLabel(new Date(entryTimestamp(e))),
          value: e.value,
        }));
      return { def, series };
    });
  }, [definitions, entries, cutoff, entryTimestamp]);

  useEffect(() => {
    setInputs((prev) => {
      const next = { ...prev };
      for (const def of definitions) {
        const ymd = dateByMetricId[def.id] ?? localDateKey(new Date());
        const existing = findNumericMetricEntryForDay(entries, def.id, ymd);
        next[def.id] = existing ? String(existing.value) : "";
      }
      return next;
    });
  }, [definitions, entries, dateByMetricId]);

  const metricsFormTodayKey = localDateKey(new Date());

  const historyDefinition = definitions.find((d) => d.id === historyDefId);
  const visibleDefinitionIds = useMemo(
    () => new Set(definitions.map((d) => d.id)),
    [definitions],
  );
  const printableMetricRecap = useMemo(() => {
    return [...entries]
      .filter(
        (e) =>
          visibleDefinitionIds.has(e.metricId) && entryTimestamp(e) >= cutoff,
      )
      .sort((a, b) => entryTimestamp(b) - entryTimestamp(a));
  }, [entries, visibleDefinitionIds, cutoff, entryTimestamp]);
  const historyEntries = useMemo(() => {
    if (!historyDefId) return [];
    return entries
      .filter((e) => e.metricId === historyDefId)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [entries, historyDefId]);

  const handlePrintRecap = () => {
    if (typeof document === "undefined" || typeof globalThis.window === "undefined") return;
    const body = document.body;
    body.setAttribute("data-print-target", "metrics-recap");
    globalThis.window.print();
    // Remove after print dialog closes/cancels.
    globalThis.setTimeout(() => {
      body.removeAttribute("data-print-target");
    }, 0);
  };

  

      const closeHistory = () => {
        setHistoryDefId(null);
        setEditingEntryId(null);
        setEditDraft("");
      }

  if (isAllProsMode) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-muted h-8 w-48 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded" />
      </div>
    );
  }

  if (!features || !resolveNumericTrackingConfigured(features)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("metrics.unavailableTitle", "Métriques")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            {t(
              "metrics.unavailableDesc",
              "Votre praticien n’a pas activé le suivi par métriques.",
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("metrics.pageTitle", "Mes métriques")}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {t(
            "metrics.pageSubtitle",
            "Une valeur par jour et par métrique : choisissez la date (y compris dans le passé), puis ajoutez ou modifiez.",
          )}
        </p>
        <NumericMetricsRangeSelector
          availableRanges={availableRanges}
          range={range}
          onRangeChange={setRange}
          rangeLabelByValue={rangeLabelByValue}
          groupLabel={t("metrics.rangeLabel", "Période")}
        />

      </div>

      <Dialog
        open={historyDefId !== null}
        onOpenChange={(open) => {
          if (!open) closeHistory();
        }}
      >
        <DialogContent className="gap-0 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
              <span className="min-w-0">
                {historyDefinition?.label ?? ""}
                {historyDefinition?.unit ? (
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    ({historyDefinition.unit})
                  </span>
                ) : null}
              </span>
              <NumericMetricDescriptionInfo
                description={historyDefinition?.description}
                ariaLabel={t(
                  "metrics.definitionInfoAria",
                  "Informations sur cette métrique",
                )}
              />
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3 pt-2">
            <p className="text-muted-foreground text-xs">
              {t(
                "metrics.historyHint",
                "Ajoutez, modifiez ou supprimez vos mesures.",
              )}
            </p>
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-medium">
                {t("metrics.addOrUpdate", "Ajouter ou modifier")}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t("metrics.measureDay", "Jour concerné")}
                  </Label>
                  <Input
                    type="date"
                    max={metricsFormTodayKey}
                    value={newEntryDate}
                    onChange={(e) => setNewEntryDate(e.target.value)}
                    className="h-9 w-full sm:w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t("metrics.newValue", "Nouvelle valeur")}
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    value={newEntryValue}
                    onChange={(e) => setNewEntryValue(e.target.value)}
                    className="h-9 w-full sm:w-32"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-9"
                  disabled={
                    appendMutation.isPending ||
                    patchMutation.isPending ||
                    !historyDefId
                  }
                  onClick={() => {
                    if (!historyDefId) return;
                    const raw = newEntryValue.trim();
                    if (!raw) return;
                    const v = Number(raw);
                    if (!Number.isFinite(v)) return;
                    const existing = findNumericMetricEntryForDay(
                      entries,
                      historyDefId,
                      newEntryDate,
                    );
                    if (existing) {
                      patchMutation.mutate({ entryId: existing.id, value: v });
                    } else {
                      appendMutation.mutate({
                        metricId: historyDefId,
                        value: v,
                        recordedOn: newEntryDate,
                      });
                    }
                    setNewEntryValue("");
                  }}
                >
                  {t("common.save", "Enregistrer")}
                </Button>
              </div>
            </div>
            {historyEntries.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-sm">
                {t("metrics.historyEmpty", "Aucune mesure pour cette métrique.")}
              </p>
            ) : (
              <ul className="max-h-[min(50dvh,320px)] space-y-2 overflow-y-auto pr-1">
                {historyEntries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-col gap-2 rounded-lg border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 shrink">
                      <p className="text-muted-foreground text-xs">
                        {new Date(entry.at).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                      {editingEntryId === entry.id ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Input
                            type="number"
                            step="any"
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            className="h-9 w-28"
                          />
                          <Button
                            type="button"
                            size="sm"
                            disabled={patchMutation.isPending}
                            onClick={() => {
                              const v = Number(editDraft.trim());
                              if (!Number.isFinite(v)) return;
                              patchMutation.mutate({
                                entryId: entry.id,
                                value: v,
                              });
                            }}
                          >
                            {t("common.save", "Enregistrer")}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingEntryId(null);
                              setEditDraft("");
                            }}
                          >
                            {t("common.cancel", "Annuler")}
                          </Button>
                        </div>
                      ) : (
                        <p className="mt-1 font-semibold tabular-nums">
                          {entry.value}
                          {historyDefinition?.unit ? (
                            <span className="text-muted-foreground ml-1 font-normal">
                              {historyDefinition.unit}
                            </span>
                          ) : null}
                        </p>
                      )}
                    </div>
                    {editingEntryId !== entry.id ? (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          aria-label={t("metrics.edit", "Modifier")}
                          onClick={() => {
                            setEditingEntryId(entry.id);
                            setEditDraft(String(entry.value));
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="text-destructive hover:text-destructive h-9 w-9"
                          aria-label={t("metrics.delete", "Supprimer")}
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (
                              typeof window !== "undefined" &&
                              !window.confirm(
                                t(
                                  "metrics.deleteConfirm",
                                  "Supprimer cette mesure ?",
                                ),
                              )
                            ) {
                              return;
                            }
                            deleteMutation.mutate(entry.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <div className="space-y-4">
        {charts.map(({ def, series }) => (
          <Card key={`chart-${def.id}`}>
            <CardHeader>
              <CardTitle className="flex items-start gap-2 text-base">
                <span className="min-w-0 flex-1">
                  {def.label}
                  {def.unit ? ` (${def.unit})` : ""}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  aria-label={t("metrics.openHistory", "Mes mesures")}
                  onClick={() => {
                    setHistoryDefId(def.id);
                    setNewEntryDate(dateByMetricId[def.id] ?? metricsFormTodayKey);
                    setNewEntryValue(inputs[def.id] ?? "");
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <NumericMetricDescriptionInfo
                  description={def.description}
                  ariaLabel={t(
                    "metrics.definitionInfoAria",
                    "Informations sur cette métrique",
                  )}
                />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {series.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t("metrics.noPoints", "Pas encore de mesure sur cette période.")}
                </p>
              ) : (
                <div className="h-[220px] w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} height={28} />
                      <YAxis width={40} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 12 }} />
                      {def.target !== undefined && Number.isFinite(def.target) ? (
                        <ReferenceLine
                          y={def.target}
                          stroke="var(--color-muted-foreground)"
                          strokeDasharray="4 4"
                        />
                      ) : null}
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="var(--color-primary)"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "var(--color-primary)" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {printableMetricRecap.length > 0 ? (
        <Card id="metrics-recap-print">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base">
              {t(
                "metrics.recapTitle",
                "Récapitulatif des mesures",
              )}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="print:hidden"
              onClick={handlePrintRecap}
            >
              <Printer className="mr-2 h-4 w-4" />
              {t("metrics.printPdf", "Imprimer ou PDF")}
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-3 text-xs">
              {t(
                "metrics.recapHint",
                "Imprimer puis « Enregistrer au format PDF » pour partager avec votre médecin ou votre coach.",
              )}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[280px] text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 pr-2 font-medium">
                      {t("metrics.recapColDate", "Date")}
                    </th>
                    <th className="pb-2 pr-2 font-medium">
                      {t("metrics.recapColMetric", "Métrique")}
                    </th>
                    <th className="pb-2 font-medium">
                      {t("metrics.recapColValue", "Valeur")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {printableMetricRecap.map((e) => {
                    const def = definitions.find((d) => d.id === e.metricId);
                    return (
                      <tr key={e.id} className="border-border/60 border-b last:border-0">
                        <td className="text-muted-foreground py-2 pr-2 align-top text-xs">
                          {new Date(e.at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </td>
                        <td className="py-2 pr-2 align-top">{def?.label ?? e.metricId}</td>
                        <td className="py-2 align-top tabular-nums font-medium">
                          {e.value}
                          {def?.unit ? ` ${def.unit}` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
